import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { DatePicker } from '../shared/DatePicker';
import { Plus, Trash2 } from 'lucide-react';

// Shared additive planning line-item editor — used by both Delivery PO and Content PO forms
export const POLineItemsEditor = ({ lineItems = [], onChange }) => {
  const addRow = () => {
    onChange([...lineItems, { description: '', delivery_date: '', amount: '', delivered: false, invoiced: false }]);
  };
  const updateRow = (idx, field, value) => {
    onChange(lineItems.map((li, i) => (i === idx ? { ...li, [field]: value } : li)));
  };
  const removeRow = (idx) => {
    onChange(lineItems.filter((_, i) => i !== idx));
  };
  const total = lineItems.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);

  return (
    <div className="space-y-2" data-testid="po-line-items-editor">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Line Items (optional planning detail)</Label>
        <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="add-line-item-btn">
          <Plus className="w-3.5 h-3.5 mr-1" />Add Line Item
        </Button>
      </div>
      {lineItems.length > 0 && (
        <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
          <div className="grid grid-cols-[1fr_130px_110px_72px_72px_32px] gap-2">
            <Label className="text-xs text-muted-foreground">Description</Label>
            <Label className="text-xs text-muted-foreground">Delivery Date</Label>
            <Label className="text-xs text-muted-foreground">Amount</Label>
            <Label className="text-xs text-muted-foreground text-center">Delivered</Label>
            <Label className="text-xs text-muted-foreground text-center">Invoiced</Label>
            <span />
          </div>
          {lineItems.map((li, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_130px_110px_72px_72px_32px] gap-2 items-center" data-testid={`line-item-row-${idx}`}>
              <Input
                value={li.description || ''}
                onChange={(e) => updateRow(idx, 'description', e.target.value)}
                placeholder="e.g. Batch 1 delivery"
                data-testid={`line-item-description-${idx}`}
              />
              <DatePicker
                value={li.delivery_date || ''}
                onChange={(v) => updateRow(idx, 'delivery_date', v)}
                placeholder="Date"
                testId={`line-item-date-${idx}`}
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                value={li.amount || ''}
                onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                placeholder="0.00"
                data-testid={`line-item-amount-${idx}`}
              />
              <div className="flex justify-center">
                <Checkbox
                  checked={!!li.delivered}
                  onCheckedChange={(v) => updateRow(idx, 'delivered', !!v)}
                  data-testid={`line-item-delivered-${idx}`}
                />
              </div>
              <div className="flex justify-center">
                <Checkbox
                  checked={!!li.invoiced}
                  onCheckedChange={(v) => updateRow(idx, 'invoiced', !!v)}
                  data-testid={`line-item-invoiced-${idx}`}
                />
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeRow(idx)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`remove-line-item-${idx}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <p className="text-xs text-muted-foreground text-right pt-1" data-testid="line-items-total">
            Line items total: ${total.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

export default POLineItemsEditor;
